import React, { useState, useEffect } from "react";
import { Container, Card, CardBody, CardHeader } from "reactstrap";
import SimpleLineChart from "../AllCharts/rechart/SimpleLineChart";
import SimpleAreaChart from "../AllCharts/rechart/SimpleAreaChart";
import MixBarChart from "../AllCharts/rechart/MixBarChart";
import VerticalComposedChart from "../AllCharts/rechart/VerticalComposedChart";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import PageGrid from "../../components/GridLayout/PageGrid";

const getCurrentShift = () => { const h=new Date().getHours(); if(h>=6&&h<14)return 1; if(h>=14&&h<22)return 2; return 3; };
const isShiftDisabled = (shift,date) => { if(date!==new Date().toISOString().split("T")[0])return false; const h=new Date().getHours(); if(shift==="1")return h<6; if(shift==="2")return h<14; if(shift==="3")return h<22&&h>=6; return false; };

const Performance = () => {
  const [machines, setMachines] = useState([]);
  const [updateChartData, setUpdateChartData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const selectedMachineId = localStorage.getItem("selectedMachineId");
  const [selectedMachine, setSelectedMachine] = useState(selectedMachineId);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [selectedShift, setSelectedShift] = useState(getCurrentShift());

  const [parts1DataMap] = useState({ goodPartsList:[], badPartsList:[], totalPartsList:[], dateTimeList:[], oeeeList:[], teepList:[], scraprateList:[], capacityUtilizationList:[] });
  const [parts2DataMap] = useState({ ...parts1DataMap });
  const [parts3DataMap] = useState({ ...parts1DataMap });
  const [dateMap] = useState({ dateTimeList:[] });

  useEffect(() => {
    fetch("https://api.hanoch.in/api/machines").then(r=>r.ok?r.json():null).then(d=>{ if(d) { setMachines(d.data||[]); if(d.data?.[33]) setSelectedMachine(d.data[33].id); } }).catch(console.error);
  }, []);

  const fetchPerformance = async () => {
    if (!selectedDate || !selectedMachine || !selectedShift) return;
    try {
      setIsLoading(true);
      const r = await fetch(`https://api.hanoch.in/production-film/oee_teep_graph/?shift=${selectedShift}&date=${selectedDate}&machine_id=${selectedMachine}`);
      if (r.ok) {
        const data = await r.json();
        setUpdateChartData({ oeeList: data.data?.map(i=>i.oee)||[], teepList: data.data?.map(i=>i.teep)||[], dateTimeList: data.data?.map(i=>i.time)||[] });
      }
    } catch(e){ console.error(e); } finally { setIsLoading(false); }
  };

  useEffect(() => { if(selectedMachine&&selectedDate&&selectedShift) fetchPerformance(); }, [selectedDate, selectedMachine, selectedShift]);

  document.title = "Performance";

  const ControlsBar = (
    <Card className="mb-0 h-100">
      <CardBody className="p-2">
        <div className="d-flex flex-wrap align-items-center gap-3" style={{ padding:"8px 12px" }}>
          <div className="d-flex align-items-center gap-1">
            <label className="text-muted mb-0" style={{ fontSize:13 }}>Machine:</label>
            <select value={selectedMachine} onChange={e=>{ setSelectedMachine(e.target.value); localStorage.setItem("selectedMachineId",e.target.value); }}
              style={{ padding:"4px 8px", border:"1px solid #30363d", borderRadius:6, fontSize:13 }}>
              <option value="">Select a machine</option>
              {machines?.map(m=><option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
          <div className="d-flex align-items-center gap-1">
            <label className="text-muted mb-0" style={{ fontSize:13 }}>Shift:</label>
            <select value={selectedShift} onChange={e=>setSelectedShift(e.target.value)}
              style={{ padding:"4px 8px", border:"1px solid #30363d", borderRadius:6, fontSize:13 }}>
              <option value="">Select shift</option>
              <option value="1" disabled={isShiftDisabled("1",selectedDate)}>6-2 (Shift 1)</option>
              <option value="2" disabled={isShiftDisabled("2",selectedDate)}>2-10 (Shift 2)</option>
              <option value="3" disabled={isShiftDisabled("3",selectedDate)}>10-6 (Shift 3)</option>
            </select>
          </div>
          <div className="d-flex align-items-center gap-1">
            <label className="text-muted mb-0" style={{ fontSize:13 }}>Date:</label>
            <input type="date" value={selectedDate} max={new Date().toISOString().split("T")[0]}
              onChange={e=>setSelectedDate(e.target.value)}
              style={{ padding:"4px 8px", border:"1px solid #30363d", borderRadius:6, fontSize:13 }}/>
          </div>
        </div>
      </CardBody>
    </Card>
  );

  const widgets = [
    { id:"controls", label:"Controls", icon:"mdi mdi-cog-outline",  defaultW:12, defaultH:3, minH:3, content: ControlsBar },
    { id:"oee",      label:"OEE Chart", icon:"mdi mdi-gauge",       defaultW:6,  defaultH:7, minH:5, content: <Card className="h-100 mb-0"><CardHeader>OVERALL EQUIPMENT EFFICIENCY (OEE)</CardHeader><CardBody><SimpleLineChart partsDataMap={updateChartData} dateMap={updateChartData}/></CardBody></Card> },
    { id:"teep",     label:"TEEP Chart", icon:"mdi mdi-chart-areaspline", defaultW:6, defaultH:7, minH:5, content: <Card className="h-100 mb-0"><CardHeader>TOTAL EQUIPMENT EFFECTIVE PERFORMANCE (TEEP)</CardHeader><CardBody><SimpleAreaChart partsDataMap={updateChartData} dateMap={updateChartData}/></CardBody></Card> },
    { id:"scrap",    label:"Scrap Rate", icon:"mdi mdi-chart-bar",  defaultW:6,  defaultH:7, minH:5, content: <Card className="h-100 mb-0"><CardHeader>SCRAP RATE</CardHeader><CardBody><MixBarChart parts1DataMap={parts1DataMap} parts2DataMap={parts2DataMap} parts3DataMap={parts3DataMap} dateMap={dateMap}/></CardBody></Card> },
    { id:"capacity", label:"Capacity Utilization", icon:"mdi mdi-percent-outline", defaultW:6, defaultH:7, minH:5, content: <Card className="h-100 mb-0"><CardHeader>CAPACITY UTILIZATION</CardHeader><CardBody><VerticalComposedChart parts1DataMap={parts1DataMap} parts2DataMap={parts2DataMap} parts3DataMap={parts3DataMap} dateMap={dateMap}/></CardBody></Card> },
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="OptiMize" breadcrumbItem="Performance" />
          <PageGrid pageKey="performance" widgets={widgets} />
        </Container>
      </div>
    </React.Fragment>
  );
};
export default Performance;
